import { CSSSelectorObjectOrCssVariables } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { useMemo } from 'react';

import { useAppearance } from '~/data/appearance';

enum CustomBackgroundSizeOption {
  Auto = 'auto',
  Cover = 'cover',
  Contain = 'contain',
}

enum CustomBackgroundPositionOption {
  Center = 'center center',
  CenterLeft = 'center left',
  CenterRight = 'center right',
  TopLeft = 'top left',
  TopCenter = 'top center',
  TopRight = 'top right',
  BottomLeft = 'bottom left',
  BottomCenter = 'bottom center',
  BottomRight = 'bottom right',
}

enum CustomBackgroundRepeatOption {
  Repeat = 'repeat',
  NoRepeat = 'no-repeat',
}

type BackgroundSettings = Pick<
  CSSSelectorObjectOrCssVariables,
  | 'backgroundImage'
  | 'backgroundColor'
  | 'backgroundPosition'
  | 'backgroundSize'
  | 'backgroundRepeat'
>;

type UseCustomBackgroundOutput = {
  backgroundSettings: BackgroundSettings;
};

export const useCustomBackground = (): UseCustomBackgroundOutput => {
  const { isLoading, customAppearance, hasData } = useAppearance();

  const { backgroundSettings } = useMemo(() => {
    const initialBackgroundSettings: BackgroundSettings = {
      backgroundImage: 'none',
      backgroundColor: '#fafafa',
      backgroundPosition: CustomBackgroundPositionOption.Center,
      backgroundSize: CustomBackgroundSizeOption.Cover,
      backgroundRepeat: CustomBackgroundRepeatOption.NoRepeat,
    };

    // Prevent drawing default picture, while custom appearance is being loaded
    if (isLoading || !hasData) {
      return {
        backgroundSettings: initialBackgroundSettings,
      };
    }

    const defaultBackgroundSettings: BackgroundSettings = {
      ...initialBackgroundSettings,
      backgroundImage: 'url("/v2/img/defaultHomepageBackground.jpg")',
    };

    const backgroundImage = customAppearance?.fAppearanceBackground
      ? `url("/d/bin/employee/profile/picture/${customAppearance.fAppearanceBackground}")`
      : undefined;

    const backgroundSettings: BackgroundSettings = !!backgroundImage
      ? {
          ...defaultBackgroundSettings,
          backgroundImage,
        }
      : defaultBackgroundSettings;

    return { backgroundSettings };
  }, [customAppearance, isLoading, hasData]);

  return {
    backgroundSettings,
  };
};
